/**
 * useMerchantPlan — React hook for reading the merchant's subscription plan.
 *
 * Subscribes to the local WatermelonDB Merchant record and returns reactive
 * plan data. Works fully offline — reads from the synced local DB.
 *
 * Safe fallback: if the merchant record is not found, returns FREE/ACTIVE
 * defaults. NEVER blocks the user.
 *
 * Usage:
 *   const { planTier, planStatus, isFrozen, isGracePeriod, daysUntilExpiry } = useMerchantPlan();
 */

import { useState, useEffect } from 'react';
import { database } from '../db';
import Merchant from '../db/models/Merchant';
import { useAppStore } from '../store/useAppStore';
import { type PlanTier, type PlanStatus, PLAN_TIERS, PLAN_STATUSES, isValidPlanTier } from './features';

export interface MerchantPlanState {
  /** Current subscription tier: FREE, BASIC, PRO, ENTERPRISE */
  planTier: PlanTier;
  /** Plan lifecycle status: ACTIVE, GRACE_PERIOD, FROZEN */
  planStatus: PlanStatus;
  /** When the plan expires (null = no expiry / lifetime) */
  planExpiresAt: Date | null;
  /** Max stores allowed by the plan */
  maxStores: number;
  /** Max users (staff) allowed by the plan */
  maxUsers: number;
  /** Max devices allowed by the plan */
  maxDevices: number;
  /** True if the account is frozen (read-only mode) */
  isFrozen: boolean;
  /** True if the account is in grace period (expiring soon) */
  isGracePeriod: boolean;
  /** Days until plan expires. null if no expiry. Negative if already expired. */
  daysUntilExpiry: number | null;
  /** True while the hook is loading the merchant record */
  isLoading: boolean;
}

/** Safe defaults — used when merchant is not found. NEVER blocks the user. */
const FREE_DEFAULTS: MerchantPlanState = {
  planTier: PLAN_TIERS.FREE,
  planStatus: PLAN_STATUSES.ACTIVE,
  planExpiresAt: null,
  maxStores: 1,
  maxUsers: 3,
  maxDevices: 1,
  isFrozen: false,
  isGracePeriod: false,
  daysUntilExpiry: null,
  isLoading: false,
};

function computeDaysUntilExpiry(expiresAt: Date | null): number | null {
  if (!expiresAt) return null;
  const now = new Date();
  const diff = expiresAt.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function normalizePlanTier(raw: string): PlanTier {
  return isValidPlanTier(raw) ? raw : PLAN_TIERS.FREE;
}

function normalizePlanStatus(raw: string): PlanStatus {
  if (Object.values(PLAN_STATUSES).includes(raw as PlanStatus)) {
    return raw as PlanStatus;
  }
  return PLAN_STATUSES.ACTIVE;
}

export function useMerchantPlan(): MerchantPlanState {
  const merchantId = useAppStore((s) => s.currentUser?.merchantId);
  const [state, setState] = useState<MerchantPlanState>({ ...FREE_DEFAULTS, isLoading: true });

  useEffect(() => {
    if (!merchantId) {
      setState(FREE_DEFAULTS);
      return;
    }

    const subscription = database
      .get<Merchant>('merchants')
      .findAndObserve(merchantId)
      .subscribe({
        next: (merchant) => {
          const planTier = normalizePlanTier(merchant.subscriptionPlan);
          const planStatus = normalizePlanStatus(merchant.planStatus);
          const planExpiresAt = merchant.planExpiresAt;
          const daysUntilExpiry = computeDaysUntilExpiry(planExpiresAt);

          setState({
            planTier,
            planStatus,
            planExpiresAt,
            maxStores: merchant.maxStores ?? 1,
            maxUsers: merchant.maxUsers ?? 3,
            maxDevices: merchant.maxDevices ?? 1,
            isFrozen: planStatus === PLAN_STATUSES.FROZEN,
            isGracePeriod: planStatus === PLAN_STATUSES.GRACE_PERIOD,
            daysUntilExpiry,
            isLoading: false,
          });
        },
        error: () => {
          // Merchant not found or DB error — use safe defaults
          setState(FREE_DEFAULTS);
        },
      });

    return () => subscription.unsubscribe();
  }, [merchantId]);

  return state;
}
